import ContentCopy from "@mui/icons-material/ContentCopy";
import Download from "@mui/icons-material/Download";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { QRCodeCanvas } from "qrcode.react";
import { useCallback, useRef, useState } from "react";
import { buildParticipantJoinUrl } from "utils/buildParticipantJoinUrl";

type Props = {
  projectId: string;
  scriptId: string;
  title?: string;
  size?: number;
};

export default function ScriptQrCode({
  projectId,
  scriptId,
  title,
  size = 160,
}: Props) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  const joinUrl = buildParticipantJoinUrl(projectId, scriptId);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }, [joinUrl]);

  const downloadPng = useCallback(() => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `${scriptId.slice(0, 8)}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }, [scriptId]);

  return (
    <Stack spacing={1.5} alignItems="center">
      {title && (
        <Typography variant="subtitle2" textAlign="center">
          {title}
        </Typography>
      )}
      <Box ref={canvasRef}>
        <QRCodeCanvas value={joinUrl} size={size} level="M" />
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          wordBreak: "break-all",
          textAlign: "center",
          maxWidth: size + 40,
        }}
      >
        {joinUrl}
      </Typography>
      <Stack direction="row" spacing={0.5}>
        <Tooltip title={copied ? "Copied" : "Copy link"}>
          <IconButton
            size="small"
            onClick={copyLink}
            aria-label="Copy join link"
          >
            <ContentCopy fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download PNG">
          <IconButton
            size="small"
            onClick={downloadPng}
            aria-label="Download QR code"
          >
            <Download fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
